import {
  Document, Page, View, Text, Image, StyleSheet, pdf, Svg, G, Circle,
} from '@react-pdf/renderer';
import { format, parseISO } from 'date-fns';
import type { Locale } from 'date-fns';
import i18next from 'i18next';
import { fmtUTCDateTime, fmtUTCDate } from '../../utils/dateTime';
import { getDateLocale } from '../../utils/dateLocale';
import type { TripExportData, ItineraryDay, Expense, PdfMeal } from '../../types';
import { CREAM, DEST_SAND, INK, MARK_DEST, MARK_DOTS, MARK_OFFSET } from '../brand/GromlyMark';

// The PDF is rendered outside the React tree (`pdf(<Doc/>)`), so it cannot use
// the useTranslation hook. It reads the i18next instance directly instead and
// resolves the matching date-fns locale once per render.
type TFunc = (key: string, opts?: Record<string, unknown>) => string;
let tr: TFunc = (key) => key;
let dateLocale: Locale | undefined;

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  navy:      '#0b1420',
  navyDim:   '#1a2a3d',
  teal:      '#0d9488',
  tealMid:   '#14b8a6',
  slate100:  '#f1f5f9',
  slate200:  '#e2e8f0',
  slate400:  '#94a3b8',
  slate600:  '#475569',
  slate900:  '#0f172a',
  white:     '#ffffff',
  amber:     '#f59e0b',
  green:     '#22c55e',
};

const s = StyleSheet.create({
  // ── Pages ──────────────────────────────────────────────────────────────
  coverPage: { backgroundColor: C.navy },
  contentPage: {
    backgroundColor: C.white,
    paddingTop: 36,
    paddingBottom: 52,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
  },

  // ── Cover image ────────────────────────────────────────────────────────
  coverImageWrapper: { position: 'relative', width: '100%', height: 260 },
  coverImage: { width: '100%', height: 260, objectFit: 'cover' },
  coverCredit: { position: 'absolute', top: 8, right: 12, fontSize: 7, color: '#ffffff', opacity: 0.85 },
  coverImageOverlay: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0, height: 100,
    backgroundColor: C.navy,
  },

  // ── Cover content ──────────────────────────────────────────────────────
  coverContent: { flex: 1, paddingHorizontal: 44, paddingBottom: 40, paddingTop: 28 },
  coverLabel: {
    fontSize: 8, letterSpacing: 3, color: C.teal,
    textTransform: 'uppercase', marginBottom: 14,
  },
  coverTitle: {
    fontSize: 32, fontFamily: 'Helvetica-Bold',
    color: C.white, lineHeight: 1.15, marginBottom: 6,
  },
  coverDest: { fontSize: 13, color: '#94a3b8', marginBottom: 3 },
  coverDates: { fontSize: 10, color: '#64748b', marginBottom: 28 },
  coverDivider: { height: 1, backgroundColor: '#1e3a5f', marginBottom: 20 },
  coverMetaRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8, gap: 0 },
  coverMetaLabel: {
    fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: 1, width: 80,
  },
  coverMetaValue: { fontSize: 9, color: '#94a3b8', flex: 1 },
  coverFooter: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 'auto', paddingTop: 24,
  },
  coverFooterText: { fontSize: 8, color: '#334155' },
  brand: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  coverFooterBrand: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: CREAM },

  // ── Page header / footer ───────────────────────────────────────────────
  pageHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingBottom: 12, marginBottom: 20,
    borderBottomWidth: 1, borderBottomColor: C.slate200, borderBottomStyle: 'solid',
  },
  pageHeaderLeft: { fontSize: 8, color: C.slate400, letterSpacing: 1 },
  pageHeaderRight: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: INK, letterSpacing: 1 },
  pageFooter: {
    position: 'absolute', bottom: 24, left: 40, right: 40,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
  },
  pageFooterText: { fontSize: 8, color: C.slate400 },

  // ── Section title ──────────────────────────────────────────────────────
  sectionTitle: {
    fontSize: 17, fontFamily: 'Helvetica-Bold', color: C.navy, marginBottom: 16,
    paddingLeft: 10,
    borderLeftWidth: 3, borderLeftColor: C.teal, borderLeftStyle: 'solid',
  },

  // ── Day block ──────────────────────────────────────────────────────────
  dayBlock: { marginBottom: 18, paddingBottom: 18, borderBottomWidth: 1, borderBottomColor: C.slate100, borderBottomStyle: 'solid' },
  dayHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 10 },
  dayBadge: {
    width: 26, height: 26, backgroundColor: C.navy, borderRadius: 13,
    alignItems: 'center', justifyContent: 'center',
  },
  dayBadgeText: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.white },
  dayTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.slate900, flex: 1 },
  dayDate: { fontSize: 9, color: C.slate400 },
  dayNotes: {
    fontSize: 8, color: C.slate600, fontStyle: 'italic',
    marginLeft: 36, marginBottom: 6, lineHeight: 1.4,
  },

  // ── Activity row ───────────────────────────────────────────────────────
  activityRow: {
    flexDirection: 'row', paddingVertical: 5, paddingLeft: 36,
    borderBottomWidth: 0.5, borderBottomColor: '#f8fafc', borderBottomStyle: 'solid',
    gap: 8, alignItems: 'flex-start',
  },
  activityTime: { fontSize: 8, color: C.slate400, width: 44, flexShrink: 0, marginTop: 1 },
  activityInfo: { flex: 1 },
  activityTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.slate900 },
  activitySub: { fontSize: 7.5, color: C.slate400, marginTop: 1.5 },
  activityTypeBadge: {
    fontSize: 6.5, color: C.teal, fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 1,
  },
  activityNotes: { fontSize: 7.5, color: C.slate600, fontStyle: 'italic', marginTop: 2, lineHeight: 1.3 },

  // ── Table ──────────────────────────────────────────────────────────────
  table: { marginBottom: 16 },
  tableHead: {
    flexDirection: 'row', backgroundColor: C.slate100,
    paddingVertical: 6, paddingHorizontal: 8, borderRadius: 4, marginBottom: 2,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5, paddingHorizontal: 8,
    borderBottomWidth: 0.5, borderBottomColor: C.slate100, borderBottomStyle: 'solid',
  },
  tableRowAlt: {
    flexDirection: 'row', backgroundColor: '#fafafa',
    paddingVertical: 5, paddingHorizontal: 8,
    borderBottomWidth: 0.5, borderBottomColor: C.slate100, borderBottomStyle: 'solid',
  },
  th: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.slate600, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { fontSize: 8.5, color: C.slate900 },
  tdMuted: { fontSize: 8, color: C.slate600 },

  // ── Venue row ──────────────────────────────────────────────────────────
  venueRow: {
    flexDirection: 'row', alignItems: 'flex-start',
    paddingVertical: 6, paddingHorizontal: 8,
    borderBottomWidth: 0.5, borderBottomColor: C.slate100, borderBottomStyle: 'solid', gap: 8,
  },
  venueCatBadge: {
    backgroundColor: C.slate100, borderRadius: 3, paddingVertical: 2, paddingHorizontal: 5,
    width: 72, flexShrink: 0,
  },
  venueCatText: {
    fontSize: 6.5, fontFamily: 'Helvetica-Bold', color: C.slate600,
    textTransform: 'uppercase', letterSpacing: 0.3,
  },
  venueName: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.slate900, flex: 2 },
  venueZone: { fontSize: 8, color: C.slate400, flex: 1.5 },
  venueVisited: { fontSize: 8, width: 36, textAlign: 'right' },

  // ── Expense summary cards ──────────────────────────────────────────────
  summaryRow: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  summaryCard: { flex: 1, backgroundColor: C.slate100, borderRadius: 6, padding: 12 },
  summaryLabel: {
    fontSize: 7.5, color: C.slate600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
  },
  summaryValue: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: C.navy },
  summaryUnit: { fontSize: 9, color: C.slate400 },

  // ── Debt row ───────────────────────────────────────────────────────────
  debtRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 6, paddingHorizontal: 8, gap: 8,
    borderBottomWidth: 0.5, borderBottomColor: C.slate100, borderBottomStyle: 'solid',
  },
  debtDebtor: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.slate900, flex: 1 },
  debtArrow: { fontSize: 9, color: C.slate400, width: 20, textAlign: 'center' },
  debtCreditor: { fontSize: 9, color: C.slate900, flex: 1 },
  debtAmount: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.navy, width: 72, textAlign: 'right' },

  // ── Checklist ──────────────────────────────────────────────────────────
  checklistSection: { marginBottom: 14 },
  checklistSectionTitle: {
    fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.navy, marginBottom: 6,
  },
  checklistItem: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 3, paddingLeft: 8, gap: 7,
  },
  checkBox: {
    width: 10, height: 10, borderWidth: 1, borderColor: C.slate400,
    borderStyle: 'solid', borderRadius: 2, flexShrink: 0,
  },
  checkBoxDone: {
    width: 10, height: 10, backgroundColor: C.teal,
    borderRadius: 2, flexShrink: 0,
  },
  checkLabel: { fontSize: 9, color: C.slate900 },
  checkLabelDone: { fontSize: 9, color: C.slate400 },

  // ── Tips ───────────────────────────────────────────────────────────────
  tipRow: {
    paddingVertical: 5, paddingHorizontal: 10, marginBottom: 5,
    borderLeftWidth: 2, borderLeftColor: C.slate200, borderLeftStyle: 'solid',
  },
  tipCategory: {
    fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.teal,
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2,
  },
  tipContent: { fontSize: 8.5, color: C.slate900, lineHeight: 1.45 },

  // ── Subsection heading (Hotels, Flights, etc.) ─────────────────────────
  subSectionTitle: {
    fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.navy,
    marginTop: 14, marginBottom: 8,
  },
});

// ── Helpers ──────────────────────────────────────────────────────────────────

const typeLabel        = (v: string) => tr(`itinerary.types.${v}`);
const venueCatLabel    = (v: string) => tr(`venues.categories.${v}`);
const expenseCatLabel  = (v: string) => tr(`expenses.categories.${v}`);
const bookingTypeLabel = (v: string) => tr(`bookings.types.${v}`);

function fmt(date: string | Date, pattern = 'd MMM yyyy') {
  try {
    if (typeof date === 'string') {
      return pattern.includes('H')
        ? fmtUTCDateTime(date, pattern.replace(/[\s,]*HH?:mm/g, '').trim(), dateLocale)
        : fmtUTCDate(date, pattern, dateLocale);
    }
    return format(date, pattern, { locale: dateLocale });
  } catch { return String(date); }
}

function money(amount: number | string, currency = 'EUR') {
  return `${currency} ${Number(amount).toFixed(2)}`;
}

function displayName(user?: { displayName?: string | null; username?: string } | null) {
  if (!user) return '–';
  return user.displayName || user.username || '–';
}

// ── Brand ────────────────────────────────────────────────────────────────────

function BrandMark({ size, dot }: { size: number; dot: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 100 100">
      <G transform={MARK_OFFSET}>
        {MARK_DOTS.map(d => <Circle key={d.cx} cx={d.cx} cy={d.cy} r={d.r} fill={dot} />)}
        <Circle cx={MARK_DEST.cx} cy={MARK_DEST.cy} r={MARK_DEST.r} fill={DEST_SAND} />
      </G>
    </Svg>
  );
}

// ── Page header / footer (fixed on every content page) ───────────────────────

function ContentHeader({ tripName }: { tripName: string }) {
  return (
    <View style={s.pageHeader} fixed>
      <Text style={s.pageHeaderLeft}>{tripName.toUpperCase()}</Text>
      <View style={s.brand}>
        <BrandMark size={16} dot={INK} />
        <Text style={s.pageHeaderRight}>GROMLY</Text>
      </View>
    </View>
  );
}

function ContentFooter() {
  return (
    <View style={s.pageFooter} fixed>
      <Text style={s.pageFooterText}>gromly.com</Text>
      <Text
        style={s.pageFooterText}
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
      />
    </View>
  );
}

// ── Cover page ────────────────────────────────────────────────────────────────

function CoverPage({ data }: { data: TripExportData }) {
  const { trip } = data;
  const allMembers = trip.participants.map(p => displayName(p.user));
  const budgetNum = trip.budget != null ? Number(trip.budget) : null;

  return (
    <Page size="A4" style={s.coverPage}>
      {/* Cover image */}
      {trip.coverImageUrl && (
        <View style={s.coverImageWrapper}>
          <Image style={s.coverImage} src={trip.coverImageUrl} />
          <View style={s.coverImageOverlay} />
          {trip.coverImagePhotographer && (
            <Text style={s.coverCredit}>{tr('common.photo')}: {trip.coverImagePhotographer} / Unsplash</Text>
          )}
        </View>
      )}

      {/* Main content */}
      <View style={s.coverContent}>
        <View>
          <Text style={s.coverLabel}>{tr('pdf.coverLabel')}</Text>
          <Text style={s.coverTitle}>{trip.name}</Text>
          <Text style={s.coverDest}>{trip.destination}</Text>
          <Text style={s.coverDates}>
            {fmt(trip.startDate, 'd MMM')} – {fmt(trip.endDate, 'd MMM yyyy')}
            {'  ·  '}{tr('pdf.nights', { count: trip.nights })}
          </Text>
          <View style={s.coverDivider} />
          <View style={s.coverMetaRow}>
            <Text style={s.coverMetaLabel}>{tr('pdf.travellers')}</Text>
            <Text style={s.coverMetaValue}>{allMembers.join('  ·  ')}</Text>
          </View>
          {budgetNum != null && budgetNum > 0 && (
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>{tr('pdf.budget')}</Text>
              <Text style={s.coverMetaValue}>{money(budgetNum, trip.budgetCurrency)}</Text>
            </View>
          )}
          {trip.notes ? (
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>{tr('pdf.notes')}</Text>
              <Text style={s.coverMetaValue}>{trip.notes}</Text>
            </View>
          ) : null}
        </View>

        <View style={s.coverFooter}>
          <Text style={s.coverFooterText}>
            {tr('pdf.generatedOn', { date: fmt(new Date(), 'd MMM yyyy') })}
          </Text>
          <View style={s.brand}>
            <BrandMark size={22} dot={CREAM} />
            <Text style={s.coverFooterBrand}>Gromly</Text>
          </View>
        </View>
      </View>
    </Page>
  );
}

// ── Itinerary section ─────────────────────────────────────────────────────────

function ItinerarySection({ days, tripName }: { days: ItineraryDay[]; tripName: string }) {
  if (days.length === 0) return null;
  const activeDays = days.filter(d => d.activities.length > 0);
  if (activeDays.length === 0) return null;

  return (
    <Page size="A4" style={s.contentPage}>
      <ContentHeader tripName={tripName} />
      <Text style={s.sectionTitle}>{tr('pdf.itinerary')}</Text>

      {activeDays.map(day => (
        <View key={day.id} style={s.dayBlock} wrap={false}>
          <View style={s.dayHeader}>
            <View style={s.dayBadge}>
              <Text style={s.dayBadgeText}>{day.dayNumber}</Text>
            </View>
            <Text style={s.dayTitle}>
              {day.title ? day.title : tr('itinerary.dayLabel', { n: day.dayNumber })}
            </Text>
            <Text style={s.dayDate}>{fmt(day.date, 'EEEE, MMM d')}</Text>
          </View>

          {day.notes ? <Text style={s.dayNotes}>{day.notes}</Text> : null}

          {[...day.activities].sort((a, b) => a.sortOrder - b.sortOrder).map(act => (
            <View key={act.id} style={s.activityRow}>
              <Text style={s.activityTime}>{act.startTime ?? '–'}</Text>
              <View style={s.activityInfo}>
                {/* ADR-0039: the travellers' own stop, as the agency's PDF shows it. */}
                <Text style={s.activityTitle}>{act.maskedForAgency ? tr('itinerary.maskedStop.title') : act.title}</Text>
                {(act.venueName || act.zone) ? (
                  <Text style={s.activitySub}>
                    {[act.venueName, act.zone].filter(Boolean).join('  ·  ')}
                  </Text>
                ) : null}
                <Text style={s.activityTypeBadge}>
                  {act.customType ?? typeLabel(act.type)}{act.isOptional ? `  ·  ${tr('itinerary.optional.badge')}` : ''}
                </Text>
                {act.notes ? <Text style={s.activityNotes}>{act.notes}</Text> : null}
              </View>
            </View>
          ))}
        </View>
      ))}

      <ContentFooter />
    </Page>
  );
}

// ── Venues section ────────────────────────────────────────────────────────────

function VenuesSection({ data }: { data: TripExportData }) {
  if (data.venues.length === 0) return null;

  return (
    <Page size="A4" style={s.contentPage}>
      <ContentHeader tripName={data.trip.name} />
      <Text style={s.sectionTitle}>{tr('pdf.placesToVisit')}</Text>

      <View style={s.table}>
        <View style={s.tableHead}>
          <Text style={[s.th, { width: 80 }]}>{tr('pdf.category')}</Text>
          <Text style={[s.th, { flex: 2 }]}>{tr('pdf.name')}</Text>
          <Text style={[s.th, { flex: 1.5 }]}>{tr('pdf.zone')}</Text>
          <Text style={[s.th, { width: 60 }]}>{tr('pdf.address')}</Text>
          <Text style={[s.th, { width: 36, textAlign: 'right' }]}>{tr('pdf.visited')}</Text>
        </View>

        {data.venues.map((v, i) => (
          <View key={v.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt} wrap={false}>
            <View style={[{ width: 80 }]}>
              <View style={s.venueCatBadge}>
                <Text style={s.venueCatText}>{v.customCategory ?? venueCatLabel(v.category)}</Text>
              </View>
            </View>
            <Text style={[s.td, { flex: 2 }]}>{v.name}</Text>
            <Text style={[s.tdMuted, { flex: 1.5 }]}>{v.zone ?? '–'}</Text>
            <Text style={[s.tdMuted, { width: 60 }]}>{v.address ?? '–'}</Text>
            <Text style={[s.td, { width: 36, textAlign: 'right', color: v.visited ? C.teal : C.slate400 }]}>
              {v.visited ? 'Yes' : 'No'}
            </Text>
          </View>
        ))}
      </View>

      <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
        <Text style={[s.tdMuted, { fontSize: 8 }]}>
          {data.venues.filter(v => v.visited).length} / {data.venues.length} visited
        </Text>
      </View>

      <ContentFooter />
    </Page>
  );
}

// ── Bookings section ──────────────────────────────────────────────────────────

function BookingsSection({ data }: { data: TripExportData }) {
  const hasFlights = data.flights.length > 0;
  const hasHotels = data.hotels.length > 0;
  const hasBookings = data.bookings.length > 0;
  if (!hasFlights && !hasHotels && !hasBookings) return null;

  return (
    <Page size="A4" style={s.contentPage}>
      <ContentHeader tripName={data.trip.name} />
      <Text style={s.sectionTitle}>{tr('pdf.bookings')}</Text>

      {hasFlights && (
        <>
          <Text style={s.subSectionTitle}>{tr('pdf.flights')}</Text>
          <View style={s.table}>
            <View style={s.tableHead}>
              <Text style={[s.th, { width: 60 }]}>{tr('pdf.direction')}</Text>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.route')}</Text>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.departure')}</Text>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.arrival')}</Text>
              <Text style={[s.th, { width: 70 }]}>{tr('pdf.flight')}</Text>
              <Text style={[s.th, { width: 64 }]}>{tr('pdf.ref')}</Text>
            </View>
            {data.flights.map((f, i) => (
              <View key={f.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt} wrap={false}>
                <Text style={[s.td, { width: 60 }]}>{tr(f.direction === 'OUTBOUND' ? 'pdf.outbound' : 'pdf.return')}</Text>
                <Text style={[s.td, { flex: 1 }]}>{f.departureAirport} → {f.arrivalAirport}</Text>
                <Text style={[s.tdMuted, { flex: 1 }]}>{fmt(f.departureTime, 'MMM d HH:mm')}</Text>
                <Text style={[s.tdMuted, { flex: 1 }]}>{fmt(f.arrivalTime, 'MMM d HH:mm')}</Text>
                <Text style={[s.tdMuted, { width: 70 }]}>{[f.airline, f.flightNumber].filter(Boolean).join(' ')}</Text>
                <Text style={[s.tdMuted, { width: 64 }]}>{f.bookingRef ?? '–'}</Text>
              </View>
            ))}
          </View>
        </>
      )}

      {hasHotels && (
        <>
          <Text style={s.subSectionTitle}>{tr('pdf.hotels')}</Text>
          <View style={s.table}>
            <View style={s.tableHead}>
              <Text style={[s.th, { flex: 2 }]}>{tr('pdf.name')}</Text>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.checkIn')}</Text>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.checkOut')}</Text>
              <Text style={[s.th, { width: 64 }]}>{tr('pdf.ref')}</Text>
            </View>
            {data.hotels.map((h, i) => (
              <View key={h.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt} wrap={false}>
                <Text style={[s.td, { flex: 2 }]}>{h.name}</Text>
                <Text style={[s.tdMuted, { flex: 1 }]}>{fmt(h.checkIn, 'MMM d, yyyy')}</Text>
                <Text style={[s.tdMuted, { flex: 1 }]}>{fmt(h.checkOut, 'MMM d, yyyy')}</Text>
                <Text style={[s.tdMuted, { width: 64 }]}>{h.bookingRef ?? '–'}</Text>
              </View>
            ))}
          </View>
        </>
      )}

      {hasBookings && (
        <>
          <Text style={s.subSectionTitle}>{tr('pdf.otherReservations')}</Text>
          <View style={s.table}>
            <View style={s.tableHead}>
              <Text style={[s.th, { width: 64 }]}>{tr('pdf.type')}</Text>
              <Text style={[s.th, { flex: 2 }]}>{tr('pdf.title')}</Text>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.date')}</Text>
              <Text style={[s.th, { width: 64 }]}>{tr('pdf.ref')}</Text>
            </View>
            {data.bookings.map((b, i) => (
              <View key={b.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt} wrap={false}>
                <Text style={[s.tdMuted, { width: 64 }]}>{bookingTypeLabel(b.type)}</Text>
                <Text style={[s.td, { flex: 2 }]}>{b.title}</Text>
                <Text style={[s.tdMuted, { flex: 1 }]}>{fmt(b.date, 'MMM d, yyyy')}</Text>
                <Text style={[s.tdMuted, { width: 64 }]}>{b.bookingRef ?? '–'}</Text>
              </View>
            ))}
          </View>
        </>
      )}

      <ContentFooter />
    </Page>
  );
}

// ── Expenses section ──────────────────────────────────────────────────────────

function ExpensesSection({ data }: { data: TripExportData }) {
  if (data.expenses.length === 0) return null;

  const total = data.expenses.reduce((s, e) => s + Number(e.amount), 0);
  const currency = data.expenses[0]?.currency ?? data.trip.budgetCurrency;
  const participants = 1 + data.trip.participants.length;

  return (
    <Page size="A4" style={s.contentPage}>
      <ContentHeader tripName={data.trip.name} />
      <Text style={s.sectionTitle}>{tr('pdf.expenses')}</Text>

      {/* Summary cards */}
      <View style={s.summaryRow}>
        <View style={s.summaryCard}>
          <Text style={s.summaryLabel}>{tr('pdf.totalSpent')}</Text>
          <Text style={s.summaryValue}>{currency} {total.toFixed(0)}</Text>
        </View>
        <View style={s.summaryCard}>
          <Text style={s.summaryLabel}>{tr('pdf.perPerson')}</Text>
          <Text style={s.summaryValue}>{currency} {(total / participants).toFixed(0)}</Text>
          <Text style={s.summaryUnit}>{participants} travellers</Text>
        </View>
        <View style={s.summaryCard}>
          <Text style={s.summaryLabel}>{tr('pdf.expenses')}</Text>
          <Text style={s.summaryValue}>{data.expenses.length}</Text>
        </View>
      </View>

      {/* Expense table */}
      <View style={s.table}>
        <View style={s.tableHead}>
          <Text style={[s.th, { width: 64 }]}>{tr('pdf.date')}</Text>
          <Text style={[s.th, { flex: 2 }]}>{tr('pdf.description')}</Text>
          <Text style={[s.th, { width: 80 }]}>{tr('pdf.category')}</Text>
          <Text style={[s.th, { flex: 1 }]}>{tr('pdf.paidBy')}</Text>
          <Text style={[s.th, { width: 80, textAlign: 'right' }]}>{tr('pdf.amount')}</Text>
        </View>
        {(data.expenses as Expense[]).map((e, i) => (
          <View key={e.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt} wrap={false}>
            <Text style={[s.tdMuted, { width: 64 }]}>{fmt(e.date, 'd MMM')}</Text>
            <Text style={[s.td, { flex: 2 }]}>{e.description}</Text>
            <Text style={[s.tdMuted, { width: 80 }]}>{expenseCatLabel(e.category)}</Text>
            <Text style={[s.tdMuted, { flex: 1 }]}>{displayName(e.payer)}</Text>
            <Text style={[s.td, { width: 80, textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>
              {money(e.amount, e.currency)}
            </Text>
          </View>
        ))}
      </View>

      {/* Debts */}
      {data.debts.length > 0 && (
        <>
          <Text style={[s.subSectionTitle, { marginTop: 20 }]}>{tr('pdf.balances')}</Text>
          <View style={s.table}>
            <View style={s.tableHead}>
              <Text style={[s.th, { flex: 1 }]}>{tr('pdf.owes')}</Text>
              <Text style={[s.th, { width: 20 }]}> </Text>
              <Text style={[s.th, { flex: 1 }]}>To</Text>
              <Text style={[s.th, { width: 80, textAlign: 'right' }]}>{tr('pdf.amount')}</Text>
            </View>
            {data.debts.map((d, i) => (
              <View key={d.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt} wrap={false}>
                <Text style={[s.td, { flex: 1, fontFamily: 'Helvetica-Bold' }]}>{displayName(d.debtor)}</Text>
                <Text style={[s.tdMuted, { width: 20, textAlign: 'center' }]}>→</Text>
                <Text style={[s.td, { flex: 1 }]}>{displayName(d.creditor)}</Text>
                <Text style={[s.td, { width: 80, textAlign: 'right', fontFamily: 'Helvetica-Bold', color: C.navy }]}>
                  {money(d.amount, d.currency)}
                </Text>
              </View>
            ))}
          </View>
        </>
      )}

      <ContentFooter />
    </Page>
  );
}

// ── Meals & shopping list section ─────────────────────────────────────────────

// ADR-0008: the plan hangs off the day and the list hangs off the trip, so this
// renders them as two blocks, not one nested table.
function MealsSection({ data }: { data: TripExportData }) {
  if (data.meals.length === 0 && data.grocery.length === 0) return null;

  const dayNumberById = new Map(data.days.map(d => [d.id, d.dayNumber]));
  const byDay = new Map<number, PdfMeal[]>();
  for (const meal of data.meals) {
    const dayNumber = dayNumberById.get(meal.dayId);
    if (dayNumber === undefined) continue;
    const list = byDay.get(dayNumber) ?? [];
    list.push(meal);
    byDay.set(dayNumber, list);
  }
  const orderedDays = [...byDay.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <Page size="A4" style={s.contentPage}>
      <ContentHeader tripName={data.trip.name} />

      {orderedDays.length > 0 && (
        <>
          <Text style={s.sectionTitle}>{tr('pdf.meals')}</Text>
          {orderedDays.map(([dayNumber, meals]) => (
            <View key={dayNumber} style={s.checklistSection} wrap={false}>
              <Text style={s.checklistSectionTitle}>{tr('itinerary.dayLabel', { n: dayNumber })}</Text>
              {meals.map(meal => (
                <View key={meal.id} style={s.checklistItem}>
                  <Text style={s.checkLabel}>
                    {tr(`meals.slot.${meal.slot}`)} · {meal.title}
                    {meal.venue ? ` (${meal.venue.name})` : ''}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </>
      )}

      {data.grocery.length > 0 && (
        <>
          <Text style={[s.sectionTitle, { marginTop: orderedDays.length > 0 ? 20 : 0 }]}>
            {tr('pdf.grocery')}
          </Text>
          <View style={s.checklistSection}>
            {data.grocery.map(item => (
              <View key={item.id} style={s.checklistItem}>
                <View style={item.bought ? s.checkBoxDone : s.checkBox} />
                <Text style={item.bought ? s.checkLabelDone : s.checkLabel}>
                  {item.text}{item.quantity ? ` — ${item.quantity}` : ''}
                </Text>
              </View>
            ))}
          </View>
        </>
      )}
    </Page>
  );
}

// ── Checklists & Tips section ─────────────────────────────────────────────────

function ChecklistsAndTipsSection({ data }: { data: TripExportData }) {
  const hasChecklists = data.checklists.some(c => c.items.length > 0);
  const hasTips = data.tips.length > 0;
  if (!hasChecklists && !hasTips) return null;

  return (
    <Page size="A4" style={s.contentPage}>
      <ContentHeader tripName={data.trip.name} />

      {hasChecklists && (
        <>
          <Text style={s.sectionTitle}>{tr('pdf.checklists')}</Text>
          {data.checklists.filter(c => c.items.length > 0).map(section => (
            <View key={section.id} style={s.checklistSection} wrap={false}>
              <Text style={s.checklistSectionTitle}>{section.title}</Text>
              {section.items.map(item => (
                <View key={item.id} style={s.checklistItem}>
                  <View style={item.checked ? s.checkBoxDone : s.checkBox} />
                  <Text style={item.checked ? s.checkLabelDone : s.checkLabel}>{item.text}</Text>
                </View>
              ))}
            </View>
          ))}
        </>
      )}

      {hasTips && (
        <>
          <Text style={[s.sectionTitle, { marginTop: hasChecklists ? 20 : 0 }]}>{tr('pdf.tips')}</Text>
          {data.tips.map(tip => (
            <View key={tip.id} style={s.tipRow} wrap={false}>
              <Text style={s.tipCategory}>{tip.category}</Text>
              <Text style={s.tipContent}>{tip.content}</Text>
            </View>
          ))}
        </>
      )}

      <ContentFooter />
    </Page>
  );
}

// ── Main document ─────────────────────────────────────────────────────────────

export function TripPdfDocument({ data }: { data: TripExportData }) {
  return (
    <Document title={data.trip.name} author="Gromly" subject={data.trip.destination}>
      <CoverPage data={data} />
      <ItinerarySection days={data.days} tripName={data.trip.name} />
      <VenuesSection data={data} />
      <BookingsSection data={data} />
      <ExpensesSection data={data} />
      <MealsSection data={data} />
      <ChecklistsAndTipsSection data={data} />
    </Document>
  );
}

// ── Download helper ───────────────────────────────────────────────────────────

export async function downloadTripPdf(data: TripExportData): Promise<void> {
  tr = i18next.t.bind(i18next) as TFunc;
  dateLocale = getDateLocale(i18next.language);
  const blob = await pdf(<TripPdfDocument data={data} />).toBlob();
  const url = URL.createObjectURL(blob);
  const slug = data.trip.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const a = document.createElement('a');
  a.href = url;
  a.download = `gromly-${slug}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
